/**
 * The offline machinery of the register, started once in the layout (T07-33):
 *
 * - the connectivity monitor (heartbeat `GET /pos/registers/current` every 15 s + `navigator.onLine`
 *   + the live stream) → green / yellow / red for every screen;
 * - the sync worker: uploads the queue as soon as the register is online again, then every 30 s
 *   while something is still queued (a `pending` op);
 * - the 仮領収書 template (`GET /pos/registers/current/offline-receipt`) kept in IndexedDB while
 *   online, so a receipt can be printed when the network is gone.
 */
import { useRealtimeStatus } from "@tcg/realtime/react";
import { useQueryClient } from "@tanstack/react-query";
import { useLiveQuery } from "dexie-react-hooks";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { registerKey, useAuthState, usePos, useRegister } from "@/app/context";
import { call } from "@/lib/api";
import { readSetting, writeSetting, type PosDatabase } from "@/lib/db";
import type { OfflineReceiptTemplate } from "@/lib/types";

import { ConnectivityMonitor, ConnectivityProvider, useConnectivity } from "./connectivity";
import { SYNC_RETRY_MS, SyncWorker, type SyncState } from "./syncWorker";

const SyncContext = createContext<SyncWorker | null>(null);

const idle: SyncState = { running: false, lastRunAt: null, last: null };

export function useSyncWorker(): SyncWorker | null {
  return useContext(SyncContext);
}

export function useSyncState(): SyncState {
  const worker = useContext(SyncContext);
  return useSyncExternalStore(
    worker?.subscribe ?? (() => () => undefined),
    worker?.getState ?? (() => idle),
    () => idle,
  );
}

export function templateKey(width: number, locale: string): string {
  return `offlineReceipt:${String(width)}:${locale}`;
}

export async function readTemplate(
  db: PosDatabase,
  width: number,
  locale: string,
): Promise<OfflineReceiptTemplate | null> {
  return (
    (await readSetting<OfflineReceiptTemplate | null>(db, templateKey(width, locale), null)) ??
    (await readSetting<OfflineReceiptTemplate | null>(db, templateKey(width, "ja"), null))
  );
}

export function OfflineRuntime({ children }: { children: ReactNode }) {
  const { api, db, auth, printer } = usePos();
  const state = useAuthState();
  const register = useRegister();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const paired = state.kind === "locked" || state.kind === "unlocked";
  const maxMinutes = register.data?.rules.offlineMaxMinutes ?? 5;

  const [monitor] = useState(
    () =>
      new ConnectivityMonitor({
        db,
        heartbeat: async () => {
          const current = await call(() => api.GET("/v1/pos/registers/current"));
          queryClient.setQueryData(registerKey, current);
          return current;
        },
      }),
  );
  const [worker] = useState(
    () =>
      new SyncWorker(db, api, () => {
        const snap = auth.getSnapshot();
        return snap.kind === "locked" || snap.kind === "unlocked" ? snap.device.deviceId : null;
      }),
  );

  // the register rule (5 min) without re-creating the monitor
  useEffect(() => {
    monitor.setMaxMinutes(maxMinutes);
  }, [monitor, maxMinutes]);

  useEffect(() => {
    if (!paired) {
      return;
    }
    void monitor.start();
    return () => monitor.stop();
  }, [monitor, paired]);

  const realtime = useRealtimeStatus();
  useEffect(() => {
    monitor.setRealtimeOffline(realtime === "offline");
  }, [monitor, realtime]);

  return (
    <ConnectivityProvider value={monitor}>
      <SyncContext.Provider value={worker}>
        <SyncDriver worker={worker} enabled={paired} />
        <TemplateKeeper
          enabled={state.kind === "unlocked"}
          width={printer.config.paperWidth}
          locale={i18n.resolvedLanguage ?? "ja"}
        />
        {children}
      </SyncContext.Provider>
    </ConnectivityProvider>
  );
}

/** Screens whose numbers an upload of offline sales changes (the drawer, 取引履歴, the basket). */
const AFTER_UPLOAD: ReadonlyArray<readonly string[]> = [
  ["pos-shifts"],
  ["pos-sales"],
  ["pos-carts"],
];

function SyncDriver({ worker, enabled }: { worker: SyncWorker; enabled: boolean }) {
  const { db } = usePos();
  const queryClient = useQueryClient();
  const connectivity = useConnectivity();
  // T07-37 (real stack): the sales of the upload are on the server now — refetch what shows them
  // instead of waiting for the stream (its events of the upload may come before it reconnected)
  useEffect(
    () =>
      worker.subscribe(() => {
        const { running, last } = worker.getState();
        if (!running && last && last.ok + last.duplicate + last.conflict > 0) {
          for (const queryKey of AFTER_UPLOAD) {
            void queryClient.invalidateQueries({ queryKey });
          }
        }
      }),
    [worker, queryClient],
  );
  const queued = useLiveQuery(() => db.opQueue.count(), [db]) ?? 0;
  const online = connectivity.state === "online";
  useEffect(() => {
    if (!enabled || !online || queued === 0) {
      return;
    }
    void worker.run();
    const timer = setInterval(() => void worker.run(), SYNC_RETRY_MS);
    return () => clearInterval(timer);
  }, [enabled, online, queued, worker]);
  return null;
}

function TemplateKeeper({
  enabled,
  width,
  locale,
}: {
  enabled: boolean;
  width: 58 | 80;
  locale: string;
}) {
  const { api, db } = usePos();
  const connectivity = useConnectivity();
  const online = connectivity.state === "online";
  useEffect(() => {
    if (!enabled || !online) {
      return;
    }
    const lang = (["en", "ja", "th", "zh-Hant", "zh-Hans"] as const).find((l) => l === locale);
    const fetchTemplate = async () => {
      try {
        const template = await call(() =>
          api.GET("/v1/pos/registers/current/offline-receipt", {
            params: { query: { width, ...(lang ? { locale: lang } : {}) } },
          }),
        );
        await writeSetting(db, templateKey(width, lang ?? "ja"), template);
      } catch {
        // kept from the last time; the next sign-in tries again
      }
    };
    void fetchTemplate();
    const timer = setInterval(() => void fetchTemplate(), 30 * 60_000);
    return () => clearInterval(timer);
  }, [api, db, enabled, locale, online, width]);
  return null;
}
